import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, CircularProgress } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { CommentsList } from '~/components/organisms/CommentsList';
import { CommentInput } from '~/components/molecules/CommentInput';
import { useReviewComments } from '~/hooks/useReviewComments';
import { MentionProvider } from '~/components/contexts/MentionContext';

interface CommentsModalContextType {
  openCommentsModal: (reviewId: string) => void;
  closeCommentsModal: () => void;
}

const CommentsModalContext = createContext<CommentsModalContextType | undefined>(undefined);

export const useCommentsModal = () => {
  const context = useContext(CommentsModalContext);
  if (context === undefined) {
    throw new Error('useCommentsModal must be used within a CommentsModalProvider');
  }
  return context;
};

interface CommentsModalProviderProps {
  children: ReactNode;
}

export const CommentsModalProvider: React.FC<CommentsModalProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentReviewId, setCurrentReviewId] = useState<string | null>(null);

  const {
    createComment,
    isCreatingComment,
    commentsCount,
    isLoading,
  } = useReviewComments(currentReviewId || '', 10);

  const handleSubmitComment = (text: string) => {
    if (currentReviewId) {
      createComment({ text });
    }
  };

  const openCommentsModal = (reviewId: string) => {
    setCurrentReviewId(reviewId);
    setIsOpen(true);
  };

  const closeCommentsModal = () => {
    setIsOpen(false);
    // Don't clear immediately to allow for smooth closing animation
    setTimeout(() => {
      setCurrentReviewId(null);
    }, 300);
  };

  return (
    <CommentsModalContext.Provider value={{ openCommentsModal, closeCommentsModal }}>
      {children}
      
      {/* Global Comments Modal */}
      {currentReviewId && (
        <Modal 
          isOpen={isOpen} 
          onClose={closeCommentsModal} 
          size="3xl"
          isDismissable={true}
          placement="top-center"
          classNames={{
            base: "max-w-full sm:max-w-xl md:max-w-2xl",
            wrapper: "z-[100]",
            body: "px-3 py-2 sm:p-6",
            header: "px-3 py-2 sm:p-6",
            footer: "px-3 py-2 sm:p-6 relative",
            closeButton: "z-[101]",
          }}
        >
          <ModalContent>
            <ModalHeader className="flex flex-col gap-1 items-start border-b border-secondary-100">
              <div className="flex items-center w-full">
                <h3 className="text-lg font-semibold">
                  {t('comments.title')} 
                </h3>
                {isLoading ? (
                  <CircularProgress 
                    size="sm" 
                    color="warning" 
                    aria-label="Loading comment count" 
                    className="ml-2"
                  />
                ) : (
                  <span className="ml-2 text-sm text-secondary-500">
                    ({commentsCount || 0})
                  </span>
                )}
              </div>
            </ModalHeader>
            <ModalBody>
              <CommentsList reviewId={currentReviewId} initialLimit={10} onNavigateToUser={() => setIsOpen(false)}/>
            </ModalBody>
            <ModalFooter className="flex-col items-stretch border-t border-secondary-100 pt-4">
              <div className="relative w-full">
                <MentionProvider>
                  <CommentInput
                    onSubmit={handleSubmitComment}
                    loading={isCreatingComment}
                    handleAuth={() => setIsOpen(false)}
                  />
                </MentionProvider>
              </div>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </CommentsModalContext.Provider>
  );
}; 